import { Autocomplete, Box, Button, createFilterOptions, FormControl, Grid, InputLabel, MenuItem, Select, Slider, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { TitlePages } from '@/components/Base/TitlePage';
import KpiComponent from '@/components/Base/KpiComponent';
import PeriodFilterDash from '@/components/Base/PeriodFilterDash';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import TableBasicRankRaioInstitutions from '@/components/Base/TableBasicRankRaioInstitutions';
import RankingComponent from '@/components/Base/RankingComponent';
import FilterClass from '@/components/Base/FilterClass';
import RankingClass from '@/components/Base/RankingClass';
import TeachingPricingFilter from '@/components/Base/TeachingPricingFilter';
import KpiComponentSlider from '@/components/Base/KpiComponentSlider';
import KpiComponentAnalysis from '@/components/Base/KpiComponentAnalysis';
import KpiComponenteGrafic from '@/components/Base/KpiComponenteGrafic';
import ScoreInadimplenciaChartTurmaOcupacao from './components/ScoreInadimplenciaChartTurmaOcupacao';
import ScoreInadimplenciaChartTurmaOcupacaoInstituicao from './components/ScoreInadimplenciaChartTurmaOcupacaoInstituicao';
import TableTermometro from '@/components/Base/TableTermometro';
import TableTermometroRankingOcupacao from '@/components/Base/TableTermometroRankingOcupacao';
import IconTeacher from '@/components/icons/IconTeacher';
import TableBasicRankRaioTurmas from '@/components/Base/TableBasicRankRaioTurmas';
import { useRaioxTurmas } from '../raiox-alunos/api/getRaioxTurmas';
import { useConsolidationScoreMovimentacaoEscolar } from './api/getConsolidationScoreMovimentacaoEscolar';
import { useOcupacaoTurmasRank } from './api/getOcupacaoRankTurmas';
import { groupBy } from 'lodash';
import { useLTVOcupacao } from './api/getLTVOcupacao';
import { getApiAuth } from '@/lib/axios';


export type BasePricingItem = {
  id: number;
  nome: string;                // "Infantil IV - INF.IV - Manhã"
  total_amount: number;        // 90278.0 (faturamento base do período)
  medium_discount: number;     // 20.53 (%)
  student_quantity: number;    // 12
  serie_id: number;
  'serie__serie_nome': string; // "Infantil IV"
  'serie__turno__nome': string;// "Manhã"
};

type ApiResponse = { list: BasePricingItem[] };

export function useBasePricing(search: string) {
  const [data, setData] = useState<BasePricingItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  const q = useMemo(() => normalize(search).trim(), [search]);

  useEffect(() => {
    // debounce 300ms
    const t = setTimeout(async () => {
      try {
        setLoading(true);
        setError(null);

        controllerRef.current?.abort();
        controllerRef.current = new AbortController();
        const api = await getApiAuth(); 

        const url = q ? `base_pricing/?name=${encodeURIComponent(search)}` 
                      : `base_pricing/`;
        const res = await api.get(url, {
          headers: { 'Content-Type': 'application/json' },
          signal: controllerRef.current.signal,
        });
        setData(res.data?.list ?? []);
      } catch (e: any) {
        if (e?.name !== 'AbortError') setError(e?.message || 'Erro ao buscar');
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      clearTimeout(t);
      controllerRef.current?.abort();
    };
  }, [q, search]);

  return { options: data, loading, error };
}


const PeriodFilterWrapper = ({
  showPeriodFilter = true,
  showMonthFilter = false,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  selectedYear,
  selectedMonth,
  setSelectedYear,
  setSelectedMonth,
  listYears,
  refetchDashboard,
}) => {
  const handleRefetch = () => {
    console.log(`Refetching for ${refetchDashboard.name || 'unknown'}`);
    refetchDashboard();
  };

  return (
    <PeriodFilterDash
      showPeriodFilter={showPeriodFilter}
      showMonthFilter={showMonthFilter}
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={onStartDateChange}
      onEndDateChange={onEndDateChange}
      refetchDashboard={handleRefetch}
      listYears={listYears}
      setSelectedYear={setSelectedYear}
      setSelectedMonth={setSelectedMonth}
      selectedYear={selectedYear}
      selectedMonth={selectedMonth}
    />
  );
};

const normalize = (s: string) =>
  (s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const RaioXTurmas = () => {
  const generateLastYears = (numYears) => {
    const currentYear = dayjs().year();
    return Array.from({ length: numYears }, (_, index) => currentYear - index).reverse();
  };
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedCurriculo, setSelectedCurriculo] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [selectedNivelEnsino, setSelectedNivelEnsino] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [listYears, setListYears] = useState(generateLastYears(5));

  const daysDiff = dayjs(selectedEndDate).diff(dayjs(selectedStartDate), 'day') || null;
  const [searchClass, setSearchClass] = useState<string>('');       // texto digitado
  const { options, loading: loadingOptions } = useBasePricing(searchClass);
  const [selectedClass, setSelectedClass] = useState<BasePricingItem | null>(null);

  const [pricePct, setPricePct] = useState(10);          // +% no preço unitário
  const [studentDelta, setStudentDelta] = useState(15);  // +alunos
  const [discountPctDelta, setDiscountPctDelta] = useState(10); // +% no desconto total

  const fmtBRL = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

  const baseStudents = selectedClass?.student_quantity ?? 0;
  const baseDiscount = (selectedClass?.medium_discount ?? 0) / 100; // 0.2053
  const baseRevenue = selectedClass?.total_amount ?? 0;             // faturamento base já fornecido

  const baseUnitPrice = useMemo(() => {
    if (!baseStudents || baseDiscount >= 0.999) return 0;
    return baseRevenue / (baseStudents * (1 - baseDiscount));
  }, [baseRevenue, baseStudents, baseDiscount]);

  const projectedStudents = Math.max(0, baseStudents + studentDelta);
  const projectedUnitPrice = baseUnitPrice * (1 + pricePct / 100);
  const projectedDiscount = Math.min(0.95, Math.max(0, baseDiscount + discountPctDelta / 100));
  const projectedRevenue = projectedStudents * projectedUnitPrice * (1 - projectedDiscount);
  const revenueDelta = projectedRevenue - baseRevenue;
  const filters = {
    ...(selectedYear && { year: selectedYear }),
    ...(selectedMonth && { month: selectedMonth }),
  };

  const { data: dataTurmas, isFetched, isFetching } = useRaioxTurmas(filters);

  const filterTurmas = createFilterOptions({
    stringify: (o: any) =>
      [
        o.id,
        o.name,
        String(o.students),
        String(o.unitPrice),
        `${Math.round(o.baseDiscountPct * 100)}%`,
        normalize(o.name),
      ].join(' '),
  });

  const mapToRanking = (turmas) => {
    const imageURL = 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQ0RGtxnp6kscDWkHUCHoUTodsIx0-McMzKNA&s';
    return turmas?.slice(0, 3)?.reduce((acc, turma, index) => {
      const rankingKey = `ranking${index + 1}`;
      acc[rankingKey] = {
        img: imageURL,
        number: turma?.media,
        name: turma?.nome_turma,
      };
      return acc;
    }, {});
  };

  const rankingmoks = mapToRanking(dataTurmas?.tableTurmas);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const { data: ocupacaoRankData, isFetched: isOcupacaoFetched, isFetching: isOcupacaoFetching } = useOcupacaoTurmasRank();

  const { data, isFetched: isAlunoChartFetched, isFetching: isAlunoChartFetching } = useConsolidationScoreMovimentacaoEscolar();

  const { data: ltvOcupacao, isFetched: isLtvOcupacaoFetched, isFetching: isLtvOcupacaoFetching } = useLTVOcupacao();


  const ClassMoks = {
    TurmaAnalysis: {
      nvoicing: 5.000,
      amount: 25,
    },
    Classes: {
      EnsinoMédio1: 70,
      EnsinoMédio2: 54,
      EnsinoMédio3: 100,
      EnsinoMédio4: 34,
      EnsinoInfantil1: 80,
      EnsinoInfantil2: 90,
      EnsinoFundamental: 10,
    },
  };

  const CardClasses = ({ Img, Type, Value }) => {
    return (
      <Box
        width={'130px'}
        height={'130px'}
        p={1}
        display={'flex'}
        flexDirection={'column'}
        alignItems={'center'}
        justifyContent={'center'}
      >
        {Img}
        <Typography fontSize={15} color={'#B3B3B3'}>{Type}</Typography>
        <Typography fontSize={15} color={'#B3B3B3'}>{Value}%</Typography>
      </Box>
    );
  };

  const refetchTurmas = () => {
    console.log('Refetching Turmas data');
  };

  return (
    <>
      <Stack flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'}>
        <TitlePages title={''} />
      </Stack>

      <Tabs value={activeTab} onChange={handleTabChange} indicatorColor="primary">
        <Tab
          sx={{
            backgroundColor: activeTab === 0 ? '#fff' : '#DDDDDD',
            color: activeTab === 0 ? '#000' : '#4C4C4C',
            fontWeight: 'bold',
            borderRadius: '14px 14px 0 0',
            px: 3,
            py: 1,
            textTransform: 'none',
            '&.MuiTab-root.Mui-selected': { color: '#000' },
            '&.MuiTabs-indicator': { height: '10px !important' },
          }}
          label="Turmas"
        />
        <Tab
          sx={{
            backgroundColor: activeTab === 1 ? '#fff' : '#DDDDDD',
            color: activeTab === 1 ? '#000' : '#4C4C4C',
            fontWeight: 'bold',
            borderRadius: '14px 14px 0 0',
            px: 3,
            py: 1,
            textTransform: 'none',
            '&.MuiTab-root.Mui-selected': { color: '#000' },
            '&.MuiTabs-indicator': { height: '10px !important' },
          }}
          label="Ocupação"
        />
        <Tab
          sx={{
            backgroundColor: activeTab === 2 ? '#fff' : '#DDDDDD',
            color: activeTab === 2 ? '#000' : '#4C4C4C',
            fontWeight: 'bold',
            borderRadius: '14px 14px 0 0',
            px: 3,
            py: 1,
            textTransform: 'none',
            '&.MuiTab-root.Mui-selected': { color: '#000' },
            '&.MuiTabs-indicator': { height: '10px !important' },
          }}
          label="Precificação de ensino"
        />
      </Tabs>

      {activeTab === 0 && (
        <Box
          padding={'15px'}
          sx={{
            border: '1px solid rgba(176, 176, 176, 1)',
            background: '#fff',
            borderRadius: '0px 0px 14px 14px',
          }}
        >
          <Stack flexDirection={'row'} gap={'8px'} alignItems="center" marginBottom="15px">
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Turmas
            </Typography>
          </Stack>

          <Stack
            flexDirection="row"
            gap="8px"
            sx={{
              overflowX: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: '#E04A4C #f1f1f1',
              '&::-webkit-scrollbar': {
                height: '6px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#E04A4C',
                borderRadius: '10px',
              },
            }}
          >
            <KpiComponent title={'Média de notas #1'} value={dataTurmas?.kpis?.media_notas} />
            <KpiComponent title={'Resultados de avaliações #2 '} value={dataTurmas?.kpis?.resultado_avaliacao} />
            <KpiComponent title={'Participação em atividades #3'} value={dataTurmas?.kpis?.participacao_atividades} />
          </Stack>

          <Box marginTop={'20px'} marginBottom={'25px'}>
            <PeriodFilterWrapper
              showPeriodFilter={false}
              showMonthFilter={false}
              startDate={selectedStartDate}
              endDate={selectedEndDate}
              onStartDateChange={setSelectedStartDate}
              onEndDateChange={setSelectedEndDate}
              refetchDashboard={refetchTurmas}
              listYears={listYears}
              setSelectedYear={setSelectedYear}
              setSelectedMonth={setSelectedMonth}
              selectedYear={selectedYear}
              selectedMonth={selectedMonth}
            />
          </Box>

          <Stack flexDirection={'row'} gap={'30px'}>
            <Box width={'100%'} flex={1} display={'flex'} justifyContent={'center'}>
              {rankingmoks && <RankingClass isInstitute={true} data={rankingmoks} />}
            </Box>

            <Box width={'50%'} flex={1}>
              {dataTurmas?.tableTurmas && <TableBasicRankRaioTurmas data={dataTurmas?.tableTurmas} />}
            </Box>
          </Stack>
        </Box>
      )}

      {activeTab === 1 && (
        <Box
          padding={'15px'}
          sx={{
            border: '1px solid rgba(176, 176, 176, 1)',
            background: '#fff',
            borderRadius: '0px 0px 14px 14px',
          }}
        >
          <Stack flexDirection={'row'} gap={'8px'} alignItems="center" marginBottom="15px">
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Ocupação das turmas
            </Typography>
          </Stack>
          <Grid mt={'10px'} container spacing={2}>
            <Grid item xs={6}>
              {data && (
                <ScoreInadimplenciaChartTurmaOcupacaoInstituicao
                  color={'#15AB51'}
                  title={'Saídas X entradas de alunos da instituição '}
                  labelColor={'#E04A4C'}
                  // {
                  //   valor_score_meses: data
                  //     ?.filter((yearData) => (selectedYear ? yearData.ano === selectedYear : yearData.ano === currentYear))
                  //     .flatMap((yearData) => yearData.valor_score_meses)
                  //     .map((month) => ({
                  //       nome: month.nome,
                  //       valor: month.valor,
                  //       valor_secundario: month.valor_secundario,
                  //     })),
                  // }
                  data={data}
                />
              )}

              <Box marginTop={'10px'} marginBottom={'25px'}>
                {/* <PeriodFilterWrapper
                  startDate={selectedStartDate}
                  endDate={selectedEndDate}
                  onStartDateChange={setSelectedStartDate}
                  onEndDateChange={setSelectedEndDate}
                  refetchDashboard={refetchOcupacaoInstituicao}
                  listYears={listYears}
                  setSelectedYear={setSelectedYear}
                  setSelectedMonth={setSelectedMonth}
                  selectedYear={selectedYear}
                  selectedMonth={selectedMonth}
                /> */}
              </Box>
            </Grid>
            <Grid item xs={6}>
              <ScoreInadimplenciaChartTurmaOcupacao
                color={'#15AB51'}
                title={'LTV: Permanência de alunos por faturamento'}
                labelColor={'#7C0D21'}
                data={{
                  valor_score_meses: ltvOcupacao,
                }}
              />
              {/* <Box marginTop={'10px'} marginBottom={'25px'}>
                <PeriodFilterWrapper
                  startDate={selectedStartDate}
                  endDate={selectedEndDate}
                  onStartDateChange={setSelectedStartDate}
                  onEndDateChange={setSelectedEndDate}
                  refetchDashboard={refetchOcupacaoLTV}
                  listYears={listYears}
                  setSelectedYear={setSelectedYear}
                  setSelectedMonth={setSelectedMonth}
                  selectedYear={selectedYear}
                  selectedMonth={selectedMonth}
                />
              </Box> */}
            </Grid>
          </Grid>

          <Grid container spacing={'20px'}>
            <Grid item sm={4}>
              <TableTermometroRankingOcupacao
                type={'1'}
                data={ocupacaoRankData?.turmas_by_rank || []}
                title={'Ranking com a maior ocupação'}
              />
            </Grid>
            <Grid item sm={4}>
              <TableTermometroRankingOcupacao
                type={'2'}
                data={ocupacaoRankData?.entradas_saidas_rank?.ranking_mais_ingressantes || []}
                title={'Ranking com a maior entrada de alunos'}
              />
            </Grid>
            <Grid item sm={4}>
              <TableTermometroRankingOcupacao
                type={'3'}
                data={ocupacaoRankData?.entradas_saidas_rank?.ranking_maior_saidas || []}
                title={'Ranking com a maior saída de alunos'}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {activeTab === 2 && (
        <Box
          padding={'15px'}
          sx={{ border: '1px solid rgba(176, 176, 176, 1)', background: '#fff', borderRadius: '0 0 14px 14px' }}
        >
          <>
            <Stack flexDirection={'row'} gap={'8px'} alignItems="center" marginBottom="15px">
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Precificação de ensino
              </Typography>
            </Stack>

            <Stack flexDirection={'row'} gap={'8px'}>
              <Stack
                flexDirection="row"
                maxWidth="100%"
                gap="8px"
                style={{ overflowX: 'auto', scrollBehavior: 'smooth', scrollbarWidth: 'thin', scrollbarColor: '#E04A4C #f1f1f1' }}
              >
                <KpiComponent title={'Faturamento total da turma'} value={fmtBRL(baseRevenue)} />
                <KpiComponent title={'Desconto médio aplicado'} value={`${(baseDiscount * 100).toFixed(2)}%`} />
                <KpiComponent title={'Preço unitário estimado (bruto)'} value={fmtBRL(baseUnitPrice)} />
                <KpiComponent title={'Alunos (base)'} value={String(baseStudents)} />
              </Stack>
            </Stack>

            <Box marginBottom={'25px'} style={{ display: 'flex', gap: 12, marginTop: '30px', marginBottom: '30px' }}>
              <FormControl fullWidth>
                <Autocomplete
                  options={options}
                  loading={loadingOptions}
                  value={selectedClass}
                  onChange={(_, newValue) => setSelectedClass(newValue)}
                  onInputChange={(_, newInput) => setSearchClass(newInput)} // dispara o hook
                  getOptionLabel={(opt) => opt?.nome ?? ''}
                  isOptionEqualToValue={(a, b) => a?.id === b?.id}
                  filterOptions={(x) => x} // filtro no servidor; não filtra no cliente
                  clearOnBlur={false}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box display="flex" flexDirection="column" width="100%">
                        <Typography fontWeight={600}>{option.nome}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {option.student_quantity} alunos • {fmtBRL(option.total_amount)} faturamento • Desconto médio {option.medium_discount}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option['serie__serie_nome']} • {option['serie__turno__nome']}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Turma"
                      placeholder="Digite para buscar…"
                      size="small"
                    />
                  )}
                />
              </FormControl>

              <Button
                sx={{
                  maxWidth: '300px',
                  p: '0 5px',
                  borderRadius: '24px',
                  fontSize: '14px',
                  height: '37px',
                  color: '#fff !important',
                  width: '188px',
                  fontWeight: 600,
                  background: '#15AB51',
                  textTransform: 'capitalize',
                }}
                variant="contained"
                onClick={() => {
                  // opcional: algum side-effect quando clicar em "Buscar"
                  // como forçar um refresh com o texto atual
                }}
              >
                Buscar
              </Button>
            </Box>

            {/* Sliders inalterados */}
            <Stack flexDirection={'row'} gap={'8px'}>
              <Stack
                flexDirection="row"
                maxWidth="100%"
                gap="8px"
                style={{ overflowX: 'auto', scrollBehavior: 'smooth', scrollbarWidth: 'thin', scrollbarColor: '#E04A4C #f1f1f1' }}
              >
                <Box minWidth={280} p={1} sx={{ borderRadius: '12px', border: '1px solid #eee' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    E se aumentarmos o preço unitário (%)
                  </Typography>
                  <Slider
                    value={pricePct}
                    min={-50}
                    max={100}
                    step={1}
                    valueLabelDisplay="auto"
                    onChange={(_, v) => setPricePct(v as number)}
                  />
                  <Typography variant="body2">Atual: {pricePct}%</Typography>
                </Box>

                <Box minWidth={280} p={1} sx={{ borderRadius: '12px', border: '1px solid #eee' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    E se aumentarmos a quantidade de alunos
                  </Typography>
                  <Slider
                    value={studentDelta}
                    min={-baseStudents}
                    max={Math.max(1, baseStudents * 2)}
                    step={1}
                    valueLabelDisplay="auto"
                    onChange={(_, v) => setStudentDelta(v as number)}
                  />
                  <Typography variant="body2">Delta alunos: {studentDelta}</Typography>
                </Box>

                <Box minWidth={280} p={1} sx={{ borderRadius: '12px', border: '1px solid #eee' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    E se aumentarmos a porcentagem de desconto total (%)
                  </Typography>
                  <Slider
                    value={discountPctDelta}
                    min={-50}
                    max={50}
                    step={1}
                    valueLabelDisplay="auto"
                    onChange={(_, v) => setDiscountPctDelta(v as number)}
                  />
                  <Typography variant="body2">Delta desconto: {discountPctDelta}%</Typography>
                </Box>
              </Stack>
            </Stack>

            {/* KPIs projetados */}
            <Box marginTop={'16px'}>
              <Stack
                flexDirection="row"
                maxWidth="100%"
                gap="8px"
                style={{ overflowX: 'auto', scrollBehavior: 'smooth', scrollbarWidth: 'thin', scrollbarColor: '#E04A4C #f1f1f1' }}
              >
                <KpiComponent title={'Faturamento projetado'} value={fmtBRL(projectedRevenue)} />
                <KpiComponent title={'Variação de faturamento'} value={`${revenueDelta >= 0 ? '+' : ''}${fmtBRL(revenueDelta)}`} />
                <KpiComponent title={'Alunos projetados'} value={`${projectedStudents}`} />
                <KpiComponent title={'Preço unitário projetado'} value={fmtBRL(projectedUnitPrice)} />
                <KpiComponent title={'Desconto projetado'} value={`${(projectedDiscount * 100).toFixed(2)}%`} />
              </Stack>
            </Box>
          </>
        </Box>
      )}
    </>
  );
};

export default RaioXTurmas;